import styled from 'styled-components';
import { BeatStatus, PatternResult, ResultColor, RhythmPattern } from '../../../../../common/src/rhythm_echo.ts';
import { resultColorCss } from './rhythm_colors.ts';
import { eventStartBeats, gridColumn, RhythmRow } from './rhythm_layout.tsx';

// What one syllable shows: unlit, lit in the accent colour while the pattern plays, or a result
// colour after tapping. Indexed by onset in pattern order, the same order as patternOnsetBeats.
export type RhythmLight = 'off' | 'on' | ResultColor;

export const rhythmBlocksTestId = 'rhythm-blocks';

// The syllable block row from the "Rhythm Echo" artboards (issue #3473): one block per event, as
// wide as the event is long, a rest as an outlined empty block, ti-ti as one block holding two
// syllables that light separately. `lights` has one entry per onset; missing entries read as off.
export const RhythmBlocks = ({ pattern, lights }: { pattern: RhythmPattern; lights: RhythmLight[] }) => {
  const starts = eventStartBeats(pattern);
  let onsetIndex = 0;
  return (
    <RhythmRow data-testid={rhythmBlocksTestId}>
      {pattern.map((event, eventIndex) => {
        const start = starts[eventIndex]; // unique per event, so it serves as the key
        const column = gridColumn(start, event.beats);
        if (event.notation.rest) {
          return <RestBlock key={start} style={{ gridColumn: column }} data-event={eventIndex} data-rest="" />;
        }
        return (
          <NoteBlock key={start} style={{ gridColumn: column }} data-event={eventIndex}>
            {event.syllables.map((syllable) => {
              const onset = onsetIndex++; // unique across the row
              const light = lights[onset] ?? 'off';
              return (
                <Syllable key={onset} $light={light} data-onset={onset} data-light={light}>
                  {syllable}
                </Syllable>
              );
            })}
          </NoteBlock>
        );
      })}
    </RhythmRow>
  );
};

// Lights for the result rows: green on time, yellow early or late, red missed
export function lightsFromResult(result: PatternResult): RhythmLight[] {
  return result.beats.map(({ status }) => resultColorForStatus(status));
}

export function resultColorForStatus(status: BeatStatus): ResultColor {
  switch (status) {
    case BeatStatus.OnTime:
      return 'green';
    case BeatStatus.Early:
    case BeatStatus.Late:
      return 'yellow';
    case BeatStatus.Missed:
      return 'red';
  }
}

// Lights while a pattern plays: the onset sounding now and every onset before it are lit, so the
// row fills left to right as the bar plays and ends fully lit, matching the "What we played" row on
// the result screen. `litIndex` undefined (before the first onset) leaves every syllable off.
export function lightsForPlayback(onsetCount: number, litIndex: number | undefined): RhythmLight[] {
  return Array.from({ length: onsetCount }, (_onset, index) =>
    litIndex !== undefined && index <= litIndex ? 'on' : 'off',
  );
}

export function allLights(onsetCount: number, light: RhythmLight): RhythmLight[] {
  return Array.from({ length: onsetCount }, () => light);
}

function lightBackground(light: RhythmLight): string {
  switch (light) {
    case 'off':
      return 'var(--color-background-search)';
    case 'on':
      return 'var(--color-tab-selected)';
    default:
      return resultColorCss[light];
  }
}

// Lit accent blocks take the page background as text colour: white on deep purple in light mode,
// dark on lavender in dark mode where the accent is Nightingale 300. Result colours keep white text
// as on the canvas.
function lightText(light: RhythmLight): string {
  switch (light) {
    case 'off':
      return 'var(--color-text)';
    case 'on':
      return 'var(--color-background)';
    default:
      return 'var(--color-text-white)';
  }
}

export const rhythmBlockHeight = 64;
const rhythmBlockRadius = 10;
const shortScreen = '@media (max-height: 700px)';

const blockStyles = `
  box-sizing: border-box;
  height: ${rhythmBlockHeight}px;
  border-radius: ${rhythmBlockRadius}px;
  min-width: 0;

  ${shortScreen} {
    height: 56px;
  }
`;

const NoteBlock = styled.div`
  ${blockStyles}
  display: flex;
  overflow: hidden;
  background-color: var(--color-background-search);
`;

const RestBlock = styled.div`
  ${blockStyles}
  border: 1px solid var(--color-divider);
  background-color: transparent;
`;

const Syllable = styled.div<{ $light: RhythmLight }>`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 18px;
  white-space: nowrap;
  overflow: hidden;
  background-color: ${({ $light }) => lightBackground($light)};
  color: ${({ $light }) => lightText($light)};
  transition: background-color 60ms linear;
`;
